import { getUser } from "@/utils/permission";
import { CLIENT_TYPE } from "@/utils/subscription";

/** 登录态账号层级；后端不再返回 ADMIN */
export const ACCOUNT_ROLE = {
  SUPER_ADMIN: "SUPER_ADMIN",
  LEVEL_ONE_ADMIN: "LEVEL_ONE_ADMIN",
  LEVEL_TWO_ADMIN: "LEVEL_TWO_ADMIN",
  MEMBER: "MEMBER",
  PERSONAL: "PERSONAL",
};

/** 团队管理接口用的 roleType */
export const TEAM_ROLE_TYPE = {
  SUPER_ADMIN: 1,
  LEVEL_ONE_ADMIN: 2,
  LEVEL_TWO_ADMIN: 3,
  MEMBER: 0,
};

/** 业务角色（仅普通用户有效） */
export const BUSINESS_ROLE_ID = {
  PRODUCER: 1,
  DIRECTOR: 2,
  OPERATOR: 3,
};

/**
 * 旧缓存回退：无 accountRole 时用 clientType + roleType 推断。
 * roleType=2 一级管理员，roleType=3 二级管理员。
 */
function resolveAccountRoleFromLegacy(user = {}) {
  const clientType = Number(user.clientType);
  const roleType = Number(user.roleType);

  if (clientType === CLIENT_TYPE.PERSONAL) {
    return ACCOUNT_ROLE.PERSONAL;
  }
  if (clientType === CLIENT_TYPE.ENTERPRISE) {
    if (roleType === TEAM_ROLE_TYPE.SUPER_ADMIN) {
      return ACCOUNT_ROLE.SUPER_ADMIN;
    }
    if (roleType === TEAM_ROLE_TYPE.LEVEL_ONE_ADMIN) {
      return ACCOUNT_ROLE.LEVEL_ONE_ADMIN;
    }
    if (roleType === TEAM_ROLE_TYPE.LEVEL_TWO_ADMIN) {
      return ACCOUNT_ROLE.LEVEL_TWO_ADMIN;
    }
    return ACCOUNT_ROLE.MEMBER;
  }
  return "";
}

/**
 * 解析账号层级，优先使用登录返回的 accountRole。
 * 旧缓存里的 ADMIN 按一级管理员处理，业务侧不再判断 ADMIN。
 * @param {Object} [user]
 * @returns {string}
 */
export function resolveAccountRole(user) {
  const source = user || getUser() || {};
  const role = String(source.accountRole || "").trim().toUpperCase();
  // 历史登录缓存仍可能是 ADMIN，归一到一级管理员
  if (role === "ADMIN") {
    return ACCOUNT_ROLE.LEVEL_ONE_ADMIN;
  }
  if (
    role === ACCOUNT_ROLE.SUPER_ADMIN ||
    role === ACCOUNT_ROLE.LEVEL_ONE_ADMIN ||
    role === ACCOUNT_ROLE.LEVEL_TWO_ADMIN ||
    role === ACCOUNT_ROLE.MEMBER ||
    role === ACCOUNT_ROLE.PERSONAL
  ) {
    return role;
  }
  return resolveAccountRoleFromLegacy(source);
}

/** 当前登录用户账号层级 */
export function getAccountRole(user) {
  return resolveAccountRole(user);
}

export function isSuperAdmin(user) {
  return resolveAccountRole(user) === ACCOUNT_ROLE.SUPER_ADMIN;
}

export function isLevelOneAdmin(user) {
  return resolveAccountRole(user) === ACCOUNT_ROLE.LEVEL_ONE_ADMIN;
}

export function isLevelTwoAdmin(user) {
  return resolveAccountRole(user) === ACCOUNT_ROLE.LEVEL_TWO_ADMIN;
}

/** 一级或二级管理员（不含超管） */
export function isSubAdmin(user) {
  const role = resolveAccountRole(user);
  return (
    role === ACCOUNT_ROLE.LEVEL_ONE_ADMIN ||
    role === ACCOUNT_ROLE.LEVEL_TWO_ADMIN
  );
}

/**
 * 是否允许购买会员/开通·升级套餐。
 * 个人客户可自行购买；企业账号仅超级管理员可买。
 */
export function canPurchaseMembership(user) {
  return isPersonal(user) || isSuperAdmin(user);
}

/** 超管、一级、二级：可进团队管理并分配本人积分 */
export function isManager(user) {
  const role = resolveAccountRole(user);
  return (
    role === ACCOUNT_ROLE.SUPER_ADMIN ||
    role === ACCOUNT_ROLE.LEVEL_ONE_ADMIN ||
    role === ACCOUNT_ROLE.LEVEL_TWO_ADMIN
  );
}

/**
 * 是否允许新建项目组。
 * 仅超级管理员、管理员（一级）、子管理员（二级）；普通成员和个人客户不行。
 */
export function canCreateProjectGroup(user) {
  return isManager(user);
}

export function isMember(user) {
  return resolveAccountRole(user) === ACCOUNT_ROLE.MEMBER;
}

export function isPersonal(user) {
  return resolveAccountRole(user) === ACCOUNT_ROLE.PERSONAL;
}

/** 列表行是否为一级管理员（roleType=2） */
export function isTeamLevelOneAdminRow(row) {
  return Number(row && row.roleType) === TEAM_ROLE_TYPE.LEVEL_ONE_ADMIN;
}

/** 列表行是否为二级管理员（roleType=3） */
export function isTeamLevelTwoAdminRow(row) {
  return Number(row && row.roleType) === TEAM_ROLE_TYPE.LEVEL_TWO_ADMIN;
}

/** 列表行是否为一级或二级管理员（无业务 roleId） */
export function isTeamManagerRow(row) {
  return isTeamLevelOneAdminRow(row) || isTeamLevelTwoAdminRow(row);
}

/** 列表行是否为业务成员（roleType=0） */
export function isTeamMemberRow(row) {
  return Number(row && row.roleType) === TEAM_ROLE_TYPE.MEMBER;
}

/** 列表行是否为超级管理员（roleType=1） */
export function isTeamSuperAdminRow(row) {
  return Number(row && row.roleType) === TEAM_ROLE_TYPE.SUPER_ADMIN;
}

/**
 * 列表行账号层级对应的 i18n key（teamManagementPage.accountTypes.*）
 * @param {Object} row
 * @returns {string}
 */
export function getTeamAccountTypeKey(row) {
  if (!row || row.roleType == null || row.roleType === "") return "";
  const roleType = Number(row.roleType);
  if (roleType === TEAM_ROLE_TYPE.SUPER_ADMIN) return "superAdmin";
  if (roleType === TEAM_ROLE_TYPE.LEVEL_ONE_ADMIN) return "levelOneAdmin";
  if (roleType === TEAM_ROLE_TYPE.LEVEL_TWO_ADMIN) return "levelTwoAdmin";
  if (roleType === TEAM_ROLE_TYPE.MEMBER) return "member";
  return "";
}
